import React from 'react';

/**
 * MaskingTape — hyper-realistic piece of masking/washi tape with torn short ends,
 * straight long edges, subtle paper fiber texture, translucency and shadow.
 *
 * Props:
 *  - width, height (px)
 *  - rotation (deg)
 *  - color: 'cream' | 'red' | 'white'
 *  - className: extra positioning classes (eg "absolute -top-2 left-4")
 *  - style: extra inline style
 */
export const MaskingTape = ({
  width = 90,
  height = 26,
  rotation = -8,
  color = 'cream',
  className = '',
  style = {},
}) => {
  const palettes = {
    cream: { base: '#e8dcb5', dark: '#b8a878', light: '#f4ead0' },
    red:   { base: '#e63a4a', dark: '#a01e2a', light: '#ff6a76' },
    white: { base: '#f0eee5', dark: '#bdbab0', light: '#ffffff' },
  };
  const c = palettes[color] || palettes.cream;
  const uid = React.useId().replace(/:/g, '');

  return (
    <div
      className={className}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        transform: `rotate(${rotation}deg)`,
        filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.55)) drop-shadow(0 1px 1px rgba(0,0,0,0.35))',
        ...style,
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        style={{ overflow: 'visible' }}
      >
        <defs>
          {/* Paper fiber texture for tape body */}
          <filter id={`paper-${uid}`} x="-2%" y="-2%" width="104%" height="104%">
            <feTurbulence baseFrequency="0.9" numOctaves="2" seed="3" type="fractalNoise" />
            <feColorMatrix values="0 0 0 0 0.12  0 0 0 0 0.10  0 0 0 0 0.08  0 0 0 0.28 0" />
            <feComposite in2="SourceGraphic" operator="in" />
            <feMerge>
              <feMergeNode in="SourceGraphic" />
              <feMergeNode />
            </feMerge>
          </filter>

          {/* Long horizontal fiber lines */}
          <pattern id={`fiber-${uid}`} x="0" y="0" width="2" height="1" patternUnits="userSpaceOnUse">
            <rect width="1" height="1" fill="rgba(0,0,0,0.04)" />
            <rect x="1" width="1" height="1" fill="transparent" />
          </pattern>
        </defs>

        {/* Tape body with torn short ends (straight long edges) */}
        <path
          d={`
            M 3,1
            L ${width - 3},1
            L ${width - 1},2 L ${width - 4},4 L ${width - 1},6
            L ${width - 5},9 L ${width - 1},12
            L ${width - 3},15 L ${width - 5},18
            L ${width - 2},21 L ${width - 4},${height - 2}
            L 4,${height - 1}
            L 2,${height - 4} L 5,${height - 7} L 1,${height - 10}
            L 4,${height - 13} L 1,${height - 16}
            L 5,${height - 19} L 2,${height - 22}
            Z
          `}
          fill={c.base}
          opacity="0.88"
          filter={`url(#paper-${uid})`}
        />

        {/* Fiber lines overlay */}
        <path
          d={`
            M 3,1
            L ${width - 3},1
            L ${width - 1},2 L ${width - 4},4 L ${width - 1},6
            L ${width - 5},9 L ${width - 1},12
            L ${width - 3},15 L ${width - 5},18
            L ${width - 2},21 L ${width - 4},${height - 2}
            L 4,${height - 1}
            L 2,${height - 4} L 5,${height - 7} L 1,${height - 10}
            L 4,${height - 13} L 1,${height - 16}
            L 5,${height - 19} L 2,${height - 22}
            Z
          `}
          fill={`url(#fiber-${uid})`}
          opacity="0.5"
        />

        {/* Subtle highlights on edges */}
        <path
          d={`M 5,1.5 L ${width - 5},1.5`}
          stroke={c.light}
          strokeWidth="0.8"
          opacity="0.4"
        />
        <path
          d={`M 5,${height - 1.5} L ${width - 5},${height - 1.5}`}
          stroke={c.dark}
          strokeWidth="0.6"
          opacity="0.35"
        />
      </svg>
    </div>
  );
};
